import React from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import {
  SITE_ORIGIN,
  DEFAULT_OG_IMAGE,
  SITE_NAME,
  PAGE_SEO,
  getPageSeo,
  buildHreflangLinks,
  buildLocalBusinessJsonLd,
  buildTouristTripJsonLd,
  buildWebPageJsonLd,
  buildBreadcrumbJsonLd,
  normalizePath,
} from "./seoConfig";

/**
 * Injects crawlable <head> tags (title, description, canonical, hreflang, OG, JSON-LD).
 * Captured into static HTML by the build-time prerender step.
 */
export default function SeoHead() {
  const { pathname } = useLocation();
  const path = normalizePath(pathname);
  const seo = getPageSeo(path);
  const seoPath = path === "/" ? "/nl" : path;
  const isIndexable = Boolean(PAGE_SEO[seoPath]);
  const canonical = `${SITE_ORIGIN}${seoPath}`;
  const hreflang = buildHreflangLinks(path);
  const isHome = ["/nl", "/en", "/de", "/"].includes(path);

  const webPageLd = buildWebPageJsonLd({
    title: seo.title,
    description: seo.description,
    path: seoPath,
    lang: seo.lang,
  });

  const jsonLdBlocks = isIndexable
    ? [webPageLd, buildBreadcrumbJsonLd(path)]
    : [];
  if (isHome) {
    jsonLdBlocks.unshift(buildTouristTripJsonLd(seo.lang));
    jsonLdBlocks.unshift(buildLocalBusinessJsonLd(seo.lang));
  }

  return (
    <Helmet htmlAttributes={{ lang: seo.lang }}>
      <title>{isIndexable ? seo.title : `Page Not Found | ${SITE_NAME}`}</title>
      <meta
        name="description"
        content={isIndexable ? seo.description : "The requested page could not be found on Surflife."}
      />
      {!isIndexable && <meta name="robots" content="noindex, follow" />}
      {isIndexable && <link rel="canonical" href={canonical} />}

      {isIndexable &&
        hreflang.map(({ hreflang: lang, href }) => (
          <link key={`${lang}-${href}`} rel="alternate" hrefLang={lang} href={href} />
        ))}

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content={seo.lang === "nl" ? "nl_NL" : seo.lang === "de" ? "de_DE" : "en_US"} />
      <meta property="og:title" content={isIndexable ? seo.title : `Page Not Found | ${SITE_NAME}`} />
      <meta property="og:description" content={isIndexable ? seo.description : "Page not found"} />
      {isIndexable && <meta property="og:url" content={canonical} />}
      <meta property="og:image" content={DEFAULT_OG_IMAGE} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={isIndexable ? seo.title : `Page Not Found | ${SITE_NAME}`} />
      <meta name="twitter:description" content={isIndexable ? seo.description : "Page not found"} />
      <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />

      {jsonLdBlocks.map((data, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
}
